import { useMemo, useState, type FormEvent } from 'react';
import type {
  DeclareTemperatureIncidentInput,
  FoodContainer,
  InventorySnapshot,
  TemperatureIncident,
  TemperatureReading,
  TimeCertainty,
} from '../domain/model';
import { latestResolutionsByContainer } from '../domain/temperature';
import { storagePolicyFor } from '../domain/storage-policy';
import { useI18n } from '../i18n/I18nContext';
import type { MessageKey } from '../i18n/catalog';
import { useApp } from './AppContext';
import { Button, containerDisplayName, containerReference, Field, Icon, Modal, SelectField, snapshotDisplayLabel } from './components';

type TemperatureModal =
  | { type: 'reading'; freezerId: string }
  | { type: 'incident'; freezerId: string; maximumTemperatureC?: number }
  | { type: 'resolve'; incidentId: string; containerId: string }
  | null;

const numberOrNull = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const localDateTimeToIso = (value: string): string | null =>
  value ? new Date(value).toISOString() : null;

type I18n = ReturnType<typeof useI18n>;
const certaintyLabel = (certainty: TimeCertainty, t: I18n['t']): string =>
  t(`temperature.certainty.${certainty}` as MessageKey);
const sourceLabel = (source: TemperatureIncident['source'], t: I18n['t']): string =>
  t(`temperature.source.${source}` as MessageKey);
const incidentPeriod = (incident: TemperatureIncident, i18n: I18n): string => {
  const boundary = (label: string, value: TemperatureIncident['started']): string =>
    value.value == null
      ? i18n.t('temperature.period.unknown', { boundary: label })
      : i18n.t('temperature.period.value', { boundary: label, certainty: certaintyLabel(value.certainty, i18n.t), date: i18n.formatDate(new Date(value.value), { dateStyle: 'short', timeStyle: 'short' }) });
  return `${boundary(i18n.t('temperature.period.start'), incident.started)}${i18n.t('common.separator')}${boundary(i18n.t('temperature.period.end'), incident.ended)}`;
};

const ReadingModal = ({
  freezerId,
  onClose,
  onDeclareIncident,
}: {
  freezerId: string;
  onClose: () => void;
  onDeclareIncident: (maximumTemperatureC: number) => void;
}) => {
  const i18n = useI18n();
  const { t } = i18n;
  const { service, perform, busyKeys } = useApp();
  const [current, setCurrent] = useState('');
  const [minimum, setMinimum] = useState('');
  const [maximum, setMaximum] = useState('');
  const [resetCertainty, setResetCertainty] = useState<TimeCertainty>('unknown');
  const [resetOn, setResetOn] = useState('');
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState<TemperatureReading | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const maximumTemperatureC = numberOrNull(maximum);
    if (maximumTemperatureC == null) return;
    const reading = await perform(
      `temperature-reading:${freezerId}`,
      () =>
        service.recordTemperatureReading({
          freezerId,
          deviceLabel: t('temperature.device.default'),
          currentTemperatureC: numberOrNull(current),
          minimumTemperatureC: numberOrNull(minimum),
          maximumTemperatureC,
          resetOn: resetCertainty === 'unknown' ? null : resetOn || null,
          resetCertainty,
          note,
        }),
      { success: t('temperature.reading.saved') },
    );
    if (reading != null) setSaved(reading);
  };

  return (
    <Modal
      title={t('temperature.reading.title')}
      description={t('temperature.reading.description')}
      onClose={onClose}
    >
      {saved == null ? (
        <form className="form-stack" onSubmit={(event) => void submit(event)}>
          <div className="notice notice--neutral">
            <Icon name="info" />
            <p>{t('temperature.reading.help')}</p>
          </div>
          <div className="temperature-fields">
            <Field label={t('temperature.current')} inputMode="decimal" value={current} onChange={(event) => setCurrent(event.target.value)} placeholder={t('temperature.example.current')} />
            <Field label={t('temperature.minimum')} inputMode="decimal" value={minimum} onChange={(event) => setMinimum(event.target.value)} placeholder={t('temperature.example.minimum')} />
            <Field label={t('temperature.maximum')} inputMode="decimal" value={maximum} onChange={(event) => setMaximum(event.target.value)} placeholder={t('temperature.example.maximum')} required autoFocus />
          </div>
          <SelectField
            label={t('temperature.lastReset')}
            value={resetCertainty}
            onChange={(event) => {
              const value = event.target.value as TimeCertainty;
              setResetCertainty(value);
              if (value === 'unknown') setResetOn('');
            }}
          >
            <option value="unknown">{t('temperature.dateUnknown')}</option>
            <option value="known">{t('temperature.dateKnown')}</option>
            <option value="estimated">{t('temperature.dateEstimated')}</option>
          </SelectField>
          {resetCertainty !== 'unknown' ? (
            <Field
              label={t('temperature.resetDate', { certainty: certaintyLabel(resetCertainty, t) })}
              type="date"
              value={resetOn}
              onChange={(event) => setResetOn(event.target.value)}
              required
              hint={t('temperature.noAddedTime')}
            />
          ) : null}
          <label className="field">
            <span className="field__label">{t('common.optionalNote')}</span>
            <textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} rows={2} />
          </label>
          <Button
            type="submit"
            disabled={
              numberOrNull(maximum) == null ||
              (resetCertainty !== 'unknown' && !resetOn) ||
              busyKeys.has(`temperature-reading:${freezerId}`)
            }
          >
            {t('temperature.saveReading')}
          </Button>
        </form>
      ) : (
        <div className="form-stack" role="status">
          <div className="notice notice--warning">
            <Icon name="warning" />
            <div>
              <strong>{t('temperature.recordedMaximum', { temperature: i18n.formatNumber(saved.maximumTemperatureC) })}</strong>
              <p>{t('temperature.recordedHelp', { date: i18n.formatDate(new Date(saved.recordedAt), { dateStyle: 'short', timeStyle: 'short' }) })}</p>
            </div>
          </div>
          <div className="button-grid">
            <Button onClick={() => onDeclareIncident(saved.maximumTemperatureC)} icon={<Icon name="warning" />}>
              {t('temperature.declareIncident')}
            </Button>
            <Button variant="secondary" onClick={onClose}>{t('common.finish')}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const IncidentModal = ({
  freezerId,
  snapshot,
  maximumTemperatureC,
  onClose,
}: {
  freezerId: string;
  snapshot: InventorySnapshot;
  maximumTemperatureC?: number;
  onClose: () => void;
}) => {
  const i18n = useI18n();
  const { t, tp } = i18n;
  const { service, perform, busyKeys } = useApp();
  const [kind, setKind] = useState<TemperatureIncident['kind']>('unknown');
  const [source, setSource] = useState<TemperatureIncident['source']>(
    maximumTemperatureC == null ? 'manual_observation' : 'mechanical_min_max',
  );
  const [startCertainty, setStartCertainty] = useState<TimeCertainty>('unknown');
  const [startValue, setStartValue] = useState('');
  const [endCertainty, setEndCertainty] = useState<TimeCertainty>('unknown');
  const [endValue, setEndValue] = useState('');
  const [maximum, setMaximum] = useState(maximumTemperatureC == null ? '' : String(maximumTemperatureC));
  const [note, setNote] = useState('');

  const input: DeclareTemperatureIncidentInput = {
    freezerId,
    kind,
    source,
    started: { value: startCertainty === 'unknown' ? null : localDateTimeToIso(startValue), certainty: startCertainty },
    ended: { value: endCertainty === 'unknown' ? null : localDateTimeToIso(endValue), certainty: endCertainty },
    maximumTemperatureC: numberOrNull(maximum),
    note,
  };
  let affected: string[] = [];
  let previewError = '';
  try {
    affected = service.previewTemperatureIncident(snapshot, input);
  } catch {
    previewError = t('temperature.invalidPeriod');
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (previewError) return;
    const incident = await perform(
      `temperature-incident:${freezerId}`,
      () => service.declareTemperatureIncident(input),
      { success: tp('temperature.exposureSaved', affected.length) },
    );
    if (incident != null) onClose();
  };

  const boundaryFields = (
    label: string,
    certainty: TimeCertainty,
    setCertainty: (value: TimeCertainty) => void,
    value: string,
    setValue: (value: string) => void,
  ) => (
    <div className="boundary-fields">
      <SelectField
        label={label}
        value={certainty}
        onChange={(event) => {
          const next = event.target.value as TimeCertainty;
          setCertainty(next);
          if (next === 'unknown') setValue('');
        }}
      >
        <option value="unknown">{t('temperature.boundaryUnknown', { boundary: label })}</option>
        <option value="known">{t('temperature.boundaryKnown', { boundary: label })}</option>
        <option value="estimated">{t('temperature.boundaryEstimated', { boundary: label })}</option>
      </SelectField>
      {certainty !== 'unknown' ? (
        <Field label={t('temperature.boundaryValue', { boundary: label, certainty: certaintyLabel(certainty, t) })} type="datetime-local" value={value} onChange={(event) => setValue(event.target.value)} required hint={t('temperature.enteredTime')} />
      ) : null}
    </div>
  );

  return (
    <Modal title={t('temperature.declareIncident')} description={t('temperature.incident.description')} onClose={onClose} wide>
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <div className="notice notice--danger">
          <Icon name="warning" />
          <p>{t('temperature.incident.safety')}</p>
        </div>
        <div className="form-row">
          <SelectField label={t('temperature.incident.type')} value={kind} onChange={(event) => setKind(event.target.value as TemperatureIncident['kind'])}>
            <option value="unknown">{t('temperature.incident.unknown')}</option>
            <option value="warming">{t('temperature.incident.warming')}</option>
            <option value="power_outage">{t('temperature.incident.power_outage')}</option>
            <option value="door_open">{t('temperature.incident.door_open')}</option>
            <option value="equipment_failure">{t('temperature.incident.equipment_failure')}</option>
          </SelectField>
          <SelectField label={t('temperature.incident.source')} value={source} onChange={(event) => setSource(event.target.value as TemperatureIncident['source'])}>
            {(['manual_observation', 'mechanical_min_max', 'freezer_alarm', 'known_power_outage', 'sensor_history'] as const).map((value) => <option key={value} value={value}>{sourceLabel(value, t)}</option>)}
          </SelectField>
        </div>
        <div className="form-row">
          {boundaryFields(t('temperature.period.start'), startCertainty, setStartCertainty, startValue, setStartValue)}
          {boundaryFields(t('temperature.period.end'), endCertainty, setEndCertainty, endValue, setEndValue)}
        </div>
        <Field label={t('temperature.maximumOptional')} inputMode="decimal" value={maximum} onChange={(event) => setMaximum(event.target.value)} />
        <label className="field">
          <span className="field__label">{t('common.optionalNote')}</span>
          <textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={2_000} rows={3} />
        </label>
        <div className={`exposure-preview ${previewError ? 'has-error' : ''}`} aria-live="polite">
          <strong>{previewError || tp('temperature.exposurePreview', affected.length)}</strong>
          <span>{t('temperature.previewHelp')}</span>
        </div>
        <Button
          type="submit"
          variant="danger"
          disabled={
            Boolean(previewError) ||
            (startCertainty !== 'unknown' && !startValue) ||
            (endCertainty !== 'unknown' && !endValue) ||
            busyKeys.has(`temperature-incident:${freezerId}`)
          }
        >
          {t('temperature.confirmIncident')}
        </Button>
      </form>
    </Modal>
  );
};

const ResolveExposureModal = ({
  incident,
  container,
  snapshot,
  onClose,
}: {
  incident: TemperatureIncident;
  container: FoodContainer;
  snapshot: InventorySnapshot;
  onClose: () => void;
}) => {
  const { locale, t } = useI18n();
  const { service, perform, busyKeys } = useApp();
  const [authorLocal, setAuthorLocal] = useState(() => t('temperature.localAuthorDefault'));
  const [choice, setChoice] = useState<'cleared' | 'keep_reviewing'>('cleared');
  const [comment, setComment] = useState('');
  const key = `temperature-resolution:${incident.id}:${container.id}`;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const result = await perform(
      key,
      () => service.resolveIncidentExposure({ incidentId: incident.id, containerId: container.id, authorLocal, choice, comment }),
      { success: choice === 'cleared' ? t('temperature.resolutionCleared') : t('temperature.resolutionKept') },
    );
    if (result != null) onClose();
  };
  return (
    <Modal
      title={
        container.serialNumber == null
          ? t('temperature.reviewItem', { reference: containerReference(container, locale) })
          : t('temperature.reviewNumber', { number: container.serialNumber })
      }
      description={containerDisplayName(snapshot, container, locale)}
      onClose={onClose}
    >
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <div className="notice notice--warning">
          <Icon name="info" />
          <p>{t('temperature.resolutionSafety')}</p>
        </div>
        <SelectField label={t('temperature.localDecision')} value={choice} onChange={(event) => setChoice(event.target.value as typeof choice)}>
          <option value="cleared">{t('temperature.clearReview')}</option>
          <option value="keep_reviewing">{t('temperature.keepReviewing')}</option>
        </SelectField>
        <Field label={t('temperature.localAuthor')} value={authorLocal} onChange={(event) => setAuthorLocal(event.target.value)} maxLength={120} required />
        <label className="field">
          <span className="field__label">{t('common.optionalComment')}</span>
          <textarea value={comment} onChange={(event) => setComment(event.target.value)} maxLength={1_000} rows={3} />
        </label>
        <Button type="submit" disabled={!authorLocal.trim() || busyKeys.has(key)}>{t('temperature.saveDecision')}</Button>
      </form>
    </Modal>
  );
};

export const TemperaturePanel = ({ snapshot }: { snapshot: InventorySnapshot }) => {
  const i18n = useI18n();
  const { locale, t, tp } = i18n;
  const [modal, setModal] = useState<TemperatureModal>(null);
  const readings = useMemo(
    () => [...snapshot.temperatureReadings].sort((left, right) => right.recordedAt.localeCompare(left.recordedAt)),
    [snapshot.temperatureReadings],
  );
  const incidents = useMemo(
    () => [...snapshot.temperatureIncidents].sort((left, right) => right.createdAt.localeCompare(left.createdAt)),
    [snapshot.temperatureIncidents],
  );
  const activeIncidents = incidents.filter((incident) => incident.status !== 'resolved');
  const resolutionTarget =
    modal?.type === 'resolve'
      ? {
          incident: snapshot.temperatureIncidents.find((incident) => incident.id === modal.incidentId),
          container: snapshot.containers.find((container) => container.id === modal.containerId),
        }
      : null;

  return (
    <>
      <section className="settings-card temperature-panel">
        <div className="settings-card__heading">
          <span className="settings-icon"><Icon name="warning" /></span>
          <div><h2>{t('temperature.panel.title')}</h2><p>{t('temperature.panel.description')}</p></div>
        </div>
        <div className="freezer-temperature-list">
          {snapshot.freezers.filter((freezer) => storagePolicyFor(freezer.equipmentKind).family !== 'pantry').map((freezer) => {
            const latest = readings.find((reading) => reading.freezerId === freezer.id);
            const activeCount = activeIncidents.filter((incident) => incident.freezerId === freezer.id).length;
            const policy = storagePolicyFor(freezer.equipmentKind);
            return (
              <article className="freezer-temperature-card" key={freezer.id}>
                <div>
                  <strong>{snapshotDisplayLabel(snapshot, freezer.name, locale)}</strong>
                  <small>
                    {t('temperature.declaredReference', { value: policy.defaultDeclaredTemperatureC == null
                      ? t('temperature.checkManual')
                      : `${policy.defaultDeclaredTemperatureC > 0 ? '+' : ''}${policy.defaultDeclaredTemperatureC} °C`}
                    )}</small>
                  <span>{latest == null ? t('temperature.noReading') : t('temperature.latestMaximum', { temperature: i18n.formatNumber(latest.maximumTemperatureC), date: i18n.formatDate(new Date(latest.recordedAt), { dateStyle: 'short', timeStyle: 'short' }) })}</span>
                  {activeCount > 0 ? <small>{tp('temperature.activeIncidents', activeCount)}</small> : null}
                </div>
                <div className="button-grid">
                  <Button variant="secondary" onClick={() => setModal({ type: 'reading', freezerId: freezer.id })}>{t('temperature.manualReading')}</Button>
                  <Button variant="danger" onClick={() => setModal({ type: 'incident', freezerId: freezer.id })}>{t('temperature.declareIncident')}</Button>
                </div>
              </article>
            );
          })}
        </div>

        {activeIncidents.length > 0 ? (
          <section className="temperature-subsection" aria-labelledby="temperature-queue-title">
            <h3 id="temperature-queue-title">{t('temperature.queue')}</h3>
            {activeIncidents.map((incident) => {
              const freezer = snapshot.freezers.find((item) => item.id === incident.freezerId);
              const latest = latestResolutionsByContainer(incident.id, snapshot.incidentResolutions);
              return (
                <article className="incident-card" key={incident.id}>
                  <header>
                    <div><strong>{freezer?.name ?? t('temperature.unknownAppliance')}</strong><span>{sourceLabel(incident.source, t)}{t('common.separator')}{incidentPeriod(incident, i18n)}</span></div>
                    <span className="incident-status">{incident.status === 'partially_resolved' ? t('temperature.inProgress') : t('temperature.toReview')}</span>
                  </header>
                  {incident.note ? <p>{snapshotDisplayLabel(snapshot, incident.note, locale)}</p> : null}
                  <ul className="exposure-list">
                    {incident.affectedContainerIds.map((containerId) => {
                      const container = snapshot.containers.find((item) => item.id === containerId);
                      if (container == null) return null;
                      const cleared = latest.get(containerId)?.choice === 'cleared';
                      return (
                        <li key={containerId}>
                          <span><strong>{containerReference(container, locale)} — {containerDisplayName(snapshot, container, locale)}</strong><small>{cleared ? t('temperature.reviewCleared') : t('temperature.excludedMeals')}</small></span>
                          {!cleared ? (
                            <Button variant="secondary" onClick={() => setModal({ type: 'resolve', incidentId: incident.id, containerId })}>{t('temperature.examine')}</Button>
                          ) : <Icon name="check" />}
                        </li>
                      );
                    })}
                  </ul>
                </article>
              );
            })}
          </section>
        ) : null}

        <details className="temperature-help">
          <summary>{t('temperature.help.title')}</summary>
          <ul className="source-list">
            <li><strong>{t('temperature.help.mechanical')}</strong> {t('temperature.help.mechanicalDetail')}</li>
            <li><strong>{t('temperature.help.sensor')}</strong> {t('temperature.help.sensorDetail')}</li>
            <li><strong>{t('temperature.help.alarm')}</strong> {t('temperature.help.alarmDetail')}</li>
            <li><strong>{t('temperature.help.socket')}</strong> {t('temperature.help.socketDetail')}</li>
            <li><strong>{t('temperature.help.ice')}</strong> {t('temperature.help.iceDetail')}</li>
          </ul>
        </details>

        {readings.length > 0 || incidents.length > 0 ? (
          <details className="temperature-history">
            <summary>{t('temperature.historySummary', { readings: tp('temperature.readingsCount', readings.length), incidents: tp('temperature.incidentsCount', incidents.length) })}</summary>
            <ol className="timeline">
              {readings.slice(0, 50).map((reading) => (
                <li key={reading.id}>
                  <span>{i18n.formatDate(new Date(reading.recordedAt), { dateStyle: 'short', timeStyle: 'short' })}</span>
                  <strong>{t('temperature.minMax', { minimum: reading.minimumTemperatureC == null ? '—' : i18n.formatNumber(reading.minimumTemperatureC), maximum: i18n.formatNumber(reading.maximumTemperatureC) })}</strong>
                  <small>{t('temperature.resetHistory', { certainty: certaintyLabel(reading.resetCertainty, t), date: reading.resetOn == null ? '' : ` : ${i18n.formatDate(new Date(`${reading.resetOn}T12:00:00`), { dateStyle: 'short' })}` })}</small>
                </li>
              ))}
              {incidents.slice(0, 50).map((incident) => (
                <li key={incident.id}>
                  <span>{i18n.formatDate(new Date(incident.createdAt), { dateStyle: 'short', timeStyle: 'short' })}</span>
                  <strong>{t('temperature.incidentHistory', { source: sourceLabel(incident.source, t) })}</strong>
                  <small>{tp('temperature.affected', incident.affectedContainerIds.length, { period: incidentPeriod(incident, i18n) })}</small>
                </li>
              ))}
            </ol>
          </details>
        ) : null}
      </section>

      {modal?.type === 'reading' ? (
        <ReadingModal
          freezerId={modal.freezerId}
          onClose={() => setModal(null)}
          onDeclareIncident={(maximumTemperatureC) => setModal({ type: 'incident', freezerId: modal.freezerId, maximumTemperatureC })}
        />
      ) : null}
      {modal?.type === 'incident' ? (
        <IncidentModal
          freezerId={modal.freezerId}
          snapshot={snapshot}
          {...(modal.maximumTemperatureC == null
            ? {}
            : { maximumTemperatureC: modal.maximumTemperatureC })}
          onClose={() => setModal(null)}
        />
      ) : null}
      {resolutionTarget?.incident != null && resolutionTarget.container != null ? (
        <ResolveExposureModal incident={resolutionTarget.incident} container={resolutionTarget.container} snapshot={snapshot} onClose={() => setModal(null)} />
      ) : null}
    </>
  );
};
